import React, { useState, useEffect, useRef, useMemo } from "react";
import { Badge, Dropdown, Input, message, Modal, Switch, Select } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SearchOutlined,
  HomeOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  UserAddOutlined,
  BarChartOutlined,
  LogoutOutlined,
  RestOutlined,
  BankOutlined,
  FileTextOutlined,
  MenuOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { apiRequest } from "../../utils/apiClient";
import "./index.css";
import checkPosType from "../../utils/posType";
import { addProduct as addProductToCart, selectActiveCart } from "../../redux/cartSlice";
import { logoutUser, setSelectedBranch } from "../../redux/userSlice";
import { APP_VERSION, INVOICE_TYPE_POS } from "../../utils/constants";
// import logo from "../../../public/images/logo.png"

const Header = ({ setSearched, searchInputRef }) => {
  const { t, i18n } = useTranslation("common");
  const { t: tPages } = useTranslation("pages");
  const activeCart = useSelector(selectActiveCart);
  const user = useSelector((state) => state.user);
  const selectedBranchId = user?.selectedBranchId || "";
  const cartItems = activeCart?.cartItems ?? [];
  const basketNumber = cartItems.length;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  // FBR toggle state (only for POS) - persisted in localStorage for CreateInvoice
  const [enableFBR, setEnableFBR] = useState(() => {
    if (user?.invoiceType !== INVOICE_TYPE_POS) return true;
    const saved = localStorage.getItem("fbrToggleState");
    return saved !== null ? saved === "true" : true;
  });

  const [searchValue, setSearchValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const suggestionItemRefs = useRef([]);

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const businessName = user?.businessName || "";
  const posType = checkPosType(businessName);
  const invoiceType = user?.invoiceType || "";
  const logoPath = invoiceType === INVOICE_TYPE_POS ? "/images/pos.png" : "/images/digitalinvoicing.png";
  const logoWidth = 140;
  const showFBRToggle = invoiceType === INVOICE_TYPE_POS;
  const isAdmin = user?.role !== "cashier";
  const showBranchFilter = isAdmin && ["/", "/invoices", "/statistics"].includes(pathname);

  // Sync FBR state from localStorage when user/invoiceType becomes available
  useEffect(() => {
    if (showFBRToggle) {
      const saved = localStorage.getItem("fbrToggleState");
      setEnableFBR(saved !== null ? saved === "true" : true);
    }
  }, [showFBRToggle]);

  // Persist FBR toggle to localStorage (used by CreateInvoice)
  useEffect(() => {
    if (showFBRToggle) {
      localStorage.setItem("fbrToggleState", enableFBR.toString());
    }
  }, [enableFBR, showFBRToggle]);

  // Load branches for branch selector (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const load = async () => {
      try {
        setBranchesLoading(true);
        const data = await apiRequest("/api/branches", { method: "GET" });
        if (!cancelled) {
          setBranches(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setBranches([]);
        }
      } finally {
        if (!cancelled) {
          setBranchesLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // Auto-select single branch so single-branch owners can edit ledger etc. without manual selection
  useEffect(() => {
    if (isAdmin && branches.length === 1 && !selectedBranchId) {
      dispatch(setSelectedBranch(branches[0]._id));
    }
  }, [isAdmin, branches, selectedBranchId, dispatch]);

  const handleAddProductFromSearch = (product) => {
    if (!product?._id) return;

    dispatch(addProductToCart(product));

    setSearchValue("");
    setSearched("");
    setSuggestions([]);
    setIsSuggestionsOpen(false);
    setActiveIndex(-1);

    if (searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  };

  useEffect(() => {
    const term = String(searchValue || "").trim();

    if (!term) {
      setSuggestions([]);
      setIsSuggestionsOpen(false);
      setActiveIndex(-1);
      return;
    }

    let isCancelled = false;

    const timeoutId = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          limit: "10",
          page: "1",
          search: term,
        });
        if (user?.role !== "cashier" && user?.selectedBranchId) {
          params.append("branchId", user.selectedBranchId);
        }
        const data = await apiRequest(`/api/products/get-all?${params.toString()}`);
        const products = Array.isArray(data?.products) ? data.products : [];

        if (isCancelled) return;

        setSuggestions(products);
        setIsSuggestionsOpen(products.length > 0);
        setActiveIndex(products.length > 0 ? 0 : -1);
      } catch (error) {
        if (!isCancelled) {
          console.error("Search suggestions error:", error);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchValue]);

  // Ensure the active suggestion is visible when navigating with arrow keys
  useEffect(() => {
    if (!isSuggestionsOpen || activeIndex < 0) return;

    const activeEl = suggestionItemRefs.current[activeIndex];
    if (activeEl && typeof activeEl.scrollIntoView === "function") {
      activeEl.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [activeIndex, isSuggestionsOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef?.current &&
        !searchInputRef.current.input?.contains(event.target)
      ) {
        setIsSuggestionsOpen(false);
      }
    };

    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchInputRef]);

  const handleSearchKeyDown = (event) => {
    if (!isSuggestionsOpen || !suggestions.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (event.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        event.preventDefault();
        handleAddProductFromSearch(suggestions[activeIndex]);
      }
    } else if (event.key === "Escape") {
      setIsSuggestionsOpen(false);
    }
  };

  const logout = async () => {
    Modal.confirm({
      title: t("logoutConfirmTitle"),
      content: t("logoutConfirmContent"),
      okText: t("yes"),
      cancelText: t("no"),
      onOk: async () => {
        try {
          await apiRequest("/api/auth/logout", { method: "POST" }, true);
        } catch (error) {
          console.error("Logout error:", error);
        } finally {
          dispatch(logoutUser());
          localStorage.removeItem("items");
          navigate("/login");
          message.success(t("logoutSuccess"));
        }
      },
    });
  };

  const navMenuItems = useMemo(() => {
    const items = [
      {
        key: "language",
        label: (
          <div
            className="header-nav-language-row"
            onClick={(e) => e.stopPropagation()}
          >
            <span>{t("language")}</span>
            <Switch
              size="small"
              checked={i18n.language === "ur"}
              onChange={(checked) => i18n.changeLanguage(checked ? "ur" : "en")}
              checkedChildren="اردو"
              unCheckedChildren="EN"
            />
          </div>
        ),
        onClick: ({ domEvent }) => domEvent.stopPropagation(),
      },
      ...(showFBRToggle
        ? [
            {
              key: "fbr",
              label: (
                <div
                  className="header-nav-language-row"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="header-nav-fbr-label flex flex-col min-w-0">
                    <span className="truncate">
                      {invoiceType === INVOICE_TYPE_POS && posType === true
                        ? "PRA invoice"
                        : "FBR invoice"}
                    </span>
                    <span className="text-xs text-gray-500 truncate">
                      {enableFBR ? tPages("fbrInvoiceEnabled") : tPages("simpleInvoiceNoFbr")}
                    </span>
                  </div>
                  <Switch
                    size="small"
                    checked={enableFBR}
                    onChange={setEnableFBR}
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                  />
                </div>
              ),
              onClick: ({ domEvent }) => domEvent.stopPropagation(),
            },
          ]
        : []),
      ...(showBranchFilter
        ? [
            {
              key: "branch-select",
              label: (
                <div
                  className="flex flex-col gap-2 w-[220px] max-w-[220px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="truncate">{tPages("selectBranch")}</span>
                  <Select
                    size="small"
                    className="w-full"
                    placeholder={tPages("selectBranch")}
                    allowClear
                    loading={branchesLoading}
                    value={
                      !branchesLoading && branches.some((b) => b._id === selectedBranchId)
                        ? selectedBranchId
                        : undefined
                    }
                    onChange={(v) => dispatch(setSelectedBranch(v || ""))}
                    options={
                      branchesLoading
                        ? []
                        : [
                            { value: "", label: tPages("allBranches") },
                            ...branches.map((b) => ({
                              value: b._id,
                              label: `${b.name} (${b.location})`,
                            })),
                          ]
                    }
                  />
                </div>
              ),
              onClick: ({ domEvent }) => domEvent.stopPropagation(),
            },
          ]
        : []),
      { type: "divider" },
      ...(posType === true
        ? [
            {
              key: "/dining",
              icon: <RestOutlined />,
              label: t("nav.dining"),
              className: pathname === "/dining" ? "header-nav-item-active" : "",
              onClick: () => navigate("/dining"),
            },
          ]
        : []),
      {
        key: "/customers",
        icon: <UserOutlined />,
        label: t("nav.customers"),
        className: pathname === "/customers" ? "header-nav-item-active" : "",
        onClick: () => navigate("/customers"),
      },
      ...(user?.role !== "cashier"
        ? [
            {
              key: "/statistics",
              icon: <BarChartOutlined />,
              label: t("nav.statistics"),
              className: pathname === "/statistics" ? "header-nav-item-active" : "",
              onClick: () => navigate("/statistics"),
            },
            {
              key: "/stock-management",
              icon: <AppstoreOutlined />,
              label: t("nav.manageStock"),
              className: pathname === "/stock-management" ? "header-nav-item-active" : "",
              onClick: () => navigate("/stock-management"),
            },
            {
              key: "/branches",
              icon: <BankOutlined />,
              label: t("nav.branches"),
              className: pathname === "/branches" ? "header-nav-item-active" : "",
              onClick: () => navigate("/branches"),
            },
          ]
        : []),
      ...(user?.role !== "cashier"
        ? [
            {
              key: "/create-cashier",
              icon: <UserAddOutlined />,
              label: t("nav.createCashier"),
              className: pathname === "/create-cashier" ? "header-nav-item-active" : "",
              onClick: () => navigate("/create-cashier"),
            },
          ]
        : []),
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: t("nav.logout"),
        danger: true,
        onClick: () => logout(),
      },
      { type: "divider" },
      {
        key: "version",
        label: (
          <span className="text-neutral-400 text-xs font-mono select-none" title="App version">
            v{APP_VERSION}
          </span>
        ),
        disabled: true,
      },
    ];
    return { items };
  }, [
    t,
    tPages,
    pathname,
    posType,
    navigate,
    i18n,
    showFBRToggle,
    enableFBR,
    user?.role,
    invoiceType,
    showBranchFilter,
    branches,
    branchesLoading,
    selectedBranchId,
    dispatch,
  ]);

  return (
    <div className="border-b mb-6">
      <header className="py-4 px-6 flex justify-between items-center gap-6">
        <div className="logo flex items-center gap-3">
          {businessName ? (
            <span className="header-business-name text-lg font-semibold text-gray-800 truncate max-w-[200px] sm:max-w-[280px]" title={businessName}>
              {businessName}
            </span>
          ) : null}
          <Link to="/" className="shrink-0">
            <img width={logoWidth} src={logoPath} alt="logo" />
          </Link>
        </div>
        <div
          className="header-search flex-1 max-w-xl"
          onClick={() => {
            pathname !== "/" && navigate("/");
          }}
        >
          <div className="relative" ref={dropdownRef}>
            <Input
              ref={searchInputRef}
              size="large"
              placeholder={t("nav.searchPlaceholder")}
              prefix={<SearchOutlined />}
              allowClear
              className="rounded-full max-w-[auto]"
              value={searchValue}
              onChange={(e) => {
                const value = e.target.value;
                setSearchValue(value);
                setSearched(value.toLowerCase());
              }}
              onKeyDown={handleSearchKeyDown}
            />

            {isSuggestionsOpen && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-50">
                {suggestions.map((product, index) => (
                  <button
                    key={product._id}
                    type="button"
                    ref={(el) => {
                      suggestionItemRefs.current[index] = el;
                    }}
                    className={`w-full px-3 py-2 text-left text-xs md:text-sm flex items-center justify-between hover:bg-gray-100 ${
                      index === activeIndex ? "bg-gray-100" : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleAddProductFromSearch(product);
                    }}
                  >
                    <span className="truncate max-w-[70%]">
                      {product.title || t("untitled")}
                    </span>
                    <span className="ml-2 text-[10px] md:text-xs text-gray-500 whitespace-nowrap">
                      {typeof product.price === "number"
                        ? product.price.toFixed(2)
                        : product.price}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Quick access: Home, Basket, Invoices */}
        <nav className="header-quick-nav flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => navigate("/")}
            className={`header-quick-nav-btn ${pathname === "/" ? "header-quick-nav-btn-active" : ""}`}
            title={t("nav.home")}
          >
            <HomeOutlined />
            <span className="header-quick-nav-text">{t("nav.home")}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/cart")}
            className={`header-quick-nav-btn ${pathname === "/cart" ? "header-quick-nav-btn-active" : ""}`}
            title={t("nav.cart")}
          >
            <ShoppingCartOutlined />
            {basketNumber > 0 && (
              <span className="header-quick-nav-badge">{basketNumber}</span>
            )}
            <span className="header-quick-nav-text">{t("nav.cart")}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/invoices")}
            className={`header-quick-nav-btn ${pathname === "/invoices" ? "header-quick-nav-btn-active" : ""}`}
            title={t("nav.invoices")}
          >
            <FileTextOutlined />
            <span className="header-quick-nav-text">{t("nav.invoices")}</span>
          </button>
        </nav>
        <Dropdown
          menu={navMenuItems}
          trigger={["click"]}
          placement="bottomRight"
          overlayClassName="header-nav-dropdown"
        >
          <button
            type="button"
            className="header-nav-trigger"
            aria-label={t("nav.menu")}
          >
            <MenuOutlined />
            <span className="header-nav-trigger-text">{t("nav.menu")}</span>
          </button>
        </Dropdown>
      </header>
    </div>
  );
};

export default Header;
